import { Map as ImmutableMap } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Select } from 'foundations-components/transitional/components/Select';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { getDataType, getDimensions, getMetrics } from 'reporting-data/report/configReportGetters';
import useFetchPropertyMetaData from 'reporting-data/hooks/useFetchPropertyMetaData';
import { EVENTS } from '../../../lib/events';
import I18n from 'I18n';
import { useMemo } from 'react';
import { allowedConfigTypes, applyConditionalFormattingToConfigColumn, getConfigMetricOptions, getIsConfigPropertySupportedByConditionalFormatting } from '../utils';
import { dateDurations } from 'reporting-data/constants/dateDurations';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const ConfigConditionalFormattingColumnSelect = ({
  report,
  onChange,
  selectedColumnName,
  onChangeColumn,
  tracker
}) => {
  const metrics = getMetrics(report);
  const dataType = getDataType(report);
  const dimensions = getDimensions(report);
  const propertyArgForMetaDataFetch = useMemo(() => {
    const metricProperties = metrics.map(metric => metric.get('property')).toList().toJS();
    const dimensionProperties = dimensions.toList().toJS();
    return [...metricProperties, ...dimensionProperties].map(property => ({
      dataType,
      property
    }));
  }, [dataType, dimensions, metrics]);
  const propertyMetaDataFetch = useFetchPropertyMetaData(propertyArgForMetaDataFetch);
  const options = useMemo(() => {
    if (!isSucceeded(propertyMetaDataFetch)) {
      return [];
    }
    const propertyMetaData = propertyMetaDataFetch.data[dataType];
    const metricOptions = metrics.filter(metric => {
      const property = propertyMetaData[metric.get('property')];
      return getIsConfigPropertySupportedByConditionalFormatting(property);
    }).flatMap(metric => getConfigMetricOptions(propertyMetaData[metric.get('property')], metric)).toJS();
    const dimensionOptions = dimensions.map(dimension => propertyMetaData[dimension] || ImmutableMap()).filter(property => allowedConfigTypes.includes(property.type) || property.type === 'datetime' && dateDurations.includes(property.dateDisplayHint)).map(property => ({
      value: property.name,
      text: property.label
    })).toList().toJS();
    return [...dimensionOptions, ...metricOptions];
  }, [dimensions, propertyMetaDataFetch, metrics, dataType]);
  const getTrackingColumnType = columnName => {
    if (!isSucceeded(propertyMetaDataFetch)) {
      return '';
    } else {
      var _propertyMetaDataFetc;
      return (_propertyMetaDataFetc = propertyMetaDataFetch.data[dataType][columnName]) === null || _propertyMetaDataFetc === void 0 ? void 0 : _propertyMetaDataFetc.numberDisplayHint;
    }
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "report-customization-components.customization-components.conditional-formatting.dropdown.label"
      }),
      labelTooltip: options.length === 0 ? I18n.text('report-customization-components.customization-components.conditional-formatting.dropdown.empty-state') : undefined,
      children: /*#__PURE__*/_jsx(Select, {
        "data-test-id": "conditional-formatting-column-select",
        options: options,
        disabled: options.length === 0,
        onChange: ({
          target: {
            value
          }
        }) => {
          tracker === null || tracker === void 0 || tracker.track(EVENTS.conditionalFormattingInteraction, {
            actionType: 'selectColumn',
            columnType: getTrackingColumnType(value)
          });
          const nextReport = applyConditionalFormattingToConfigColumn(report, value);
          onChangeColumn(value);
          if (nextReport) {
            onChange(nextReport);
          }
        },
        value: selectedColumnName,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "report-customization-components.customization-components.conditional-formatting.dropdown.placeholder"
        })
      })
    })
  });
};